import { FC, KeyboardEvent, useCallback, useRef } from "react";

import { InputBase, Stack, useTheme } from "@mui/material";

import { useTranslate } from "@/common/hooks/useTranslate";

const CODE_LENGTH = 6;

interface BidderOtpCodeInputProps {
  code: string;
  setCode: (code: string) => void;
  onComplete?: (code: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
}

export const BidderOtpCodeInput: FC<BidderOtpCodeInputProps> = ({
  code,
  setCode,
  onComplete,
  disabled = false,
  autoFocus = true,
}) => {
  const theme = useTheme();
  const { t } = useTranslate();
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const digits = code.split("").concat(Array(CODE_LENGTH).fill("")).slice(0, CODE_LENGTH);

  const handleChange = useCallback(
    (index: number, rawValue: string) => {
      const value = rawValue.replace(/\D/g, "");
      if (value.length > 1) {
        const pasted = value.slice(0, CODE_LENGTH);
        setCode(pasted);
        const nextIndex = Math.min(pasted.length, CODE_LENGTH - 1);
        inputRefs.current[nextIndex]?.focus();
        if (pasted.length === CODE_LENGTH) {
          onComplete?.(pasted);
        }
        return;
      }

      const newDigits = [...digits];
      newDigits[index] = value;
      const newCode = newDigits.join("");
      setCode(newCode);

      if (value && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
      if (newCode.length === CODE_LENGTH && !newCode.includes("")) {
        onComplete?.(newCode);
      }
    },
    [digits, setCode, onComplete]
  );

  const handleKeyDown = useCallback(
    (index: number, event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (event.key === "Backspace" && digits[index] === "" && index > 0) {
        const newDigits = [...digits];
        newDigits[index - 1] = "";
        setCode(newDigits.join(""));
        inputRefs.current[index - 1]?.focus();
        event.preventDefault();
      } else if (event.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
        event.preventDefault();
      } else if (event.key === "ArrowRight" && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
        event.preventDefault();
      }
    },
    [digits, setCode]
  );

  return (
    <Stack direction="row" gap={1} justifyContent="center">
      {Array.from({ length: CODE_LENGTH }, (_, index) => (
        <InputBase
          key={index}
          value={digits[index]}
          disabled={disabled}
          inputRef={(el: HTMLInputElement | null) => {
            inputRefs.current[index] = el;
          }}
          autoFocus={autoFocus && index === 0}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          inputProps={{
            inputMode: "numeric",
            maxLength: index === 0 ? CODE_LENGTH : 1,
            "aria-label": t("common", "a11y.digit", { index: index + 1 }),
            "data-test": `bidder-otp-digit-${index + 1}`,
            style: { textAlign: "center", padding: 0 },
          }}
          sx={{
            width: 48,
            height: 56,
            ...theme.typography.h5,
            color: theme.palette.formsComponents.textField.textColor,
            backgroundColor: theme.palette.formsComponents.textField.backgroundColor,
            border: `1px solid ${theme.palette.formsComponents.textField.borderColor}`,
            borderRadius: theme.spacing(1),
            transition: "outline 100ms ease-out, border-color 200ms ease-out",
            outlineColor: "transparent",
            ["&:hover:not(.Mui-disabled)"]: {
              borderColor: theme.palette.formsComponents.textField.hoverBorderColor,
            },
            ["&.Mui-focused"]: {
              borderColor: theme.palette.formsComponents.textField.hoverBorderColor,
              outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
            },
          }}
        />
      ))}
    </Stack>
  );
};
